/**
 * @file shop.ts
 *
 * Client for the commerce-engine shop endpoints, backing the admin's get-or-create
 * bootstrap. `getShop` returns the session's shop, or `null` on a 404 (the signal
 * to create one); `createShop` POSTs to create it (the backend gates on plan and
 * is idempotent). The shop is resolved server-side from the session, so neither
 * call passes an identifier.
 *
 * These endpoints are versioned (`/v1/shops`) and wrap success payloads in the
 * `{ data, meta }` response envelope, so the callers unwrap `.data`.
 */

import { apiFetch } from './client';

/** Success envelope returned by the versioned shop endpoints. */
interface ShopEnvelope {
  data: Shop;
}

/** Shop fields returned by the shop endpoints (mirrors the backend DTO). */
export interface Shop {
  id: string;
  domainId: string;
  name: string | null;
  description: string | null;
  currency: string;
  country: string | null;
  locale: string | null;
  timezone: string | null;
  senderEmail: string | null;
  receiverEmail: string | null;
  businessAddress: unknown;
  vatEnabled: boolean;
  defaultVatRate: number | null;
  applyVatToShipping: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fetches the session's shop.
 *
 * @returns The {@link Shop}, or `null` when none exists yet (HTTP 404).
 *
 * @throws {Error} When the request fails for any reason other than 404.
 */
export async function getShop(): Promise<Shop | null> {
  const response = await apiFetch('/v1/shops');
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`GET /v1/shops failed: ${response.status}`);
  }
  const envelope = (await response.json()) as ShopEnvelope;
  return envelope.data;
}

/**
 * Creates the session's shop (backend is plan-gated and idempotent).
 *
 * @returns The created (or already-existing) {@link Shop}.
 *
 * @throws {Error} When the request fails (e.g. 403 when the plan is ineligible).
 */
export async function createShop(): Promise<Shop> {
  const response = await apiFetch('/v1/shops', { method: 'POST' });
  if (!response.ok) {
    throw new Error(`POST /v1/shops failed: ${response.status}`);
  }
  const envelope = (await response.json()) as ShopEnvelope;
  return envelope.data;
}
